import { useState } from 'react'
import {
  Box,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Typography,
  Chip,
  Skeleton,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  Search,
  Person,
  Groups,
  Terrain,
} from '@mui/icons-material'
import { ConversationDto, ConversationType } from '@api/types/chat'
import { useChatStore, useFilteredConversations } from '@store/chatStore'
import { useAuthStore } from '@store/authStore'
import { formatDistanceToNow } from 'date-fns'

const FILTER_OPTIONS = [
  { key: 'all' as const, label: 'All' },
  { key: 'direct' as const, label: 'Direct' },
  { key: 'groups' as const, label: 'Groups' },
  { key: 'adventures' as const, label: 'Adventures' },
]

interface ConversationListProps {
  onConversationSelect: (conversationId: string) => void
  isLoading?: boolean
}

export function ConversationList({
  onConversationSelect,
  isLoading = false,
}: ConversationListProps) {
  const theme = useTheme()
  const [searchQuery, setSearchQuery] = useState('')
  const { activeConversationId, conversationFilter, setConversationFilter } = useChatStore()
  const { user } = useAuthStore()

  const filteredConversations = useFilteredConversations()

  // Apply search filter on top of type filter
  const searchFiltered = searchQuery.trim()
    ? filteredConversations.filter((conv) => {
        const query = searchQuery.toLowerCase()
        const otherParticipants = conv.participants.filter((p) => p.userId !== user?.userId)
        const name = conv.name || otherParticipants.map((p) => p.displayName).join(', ')
        return name.toLowerCase().includes(query)
      })
    : filteredConversations

  const getConversationDisplayInfo = (conversation: ConversationDto) => {
    const otherParticipants = conversation.participants.filter(
      (p) => p.userId !== user?.userId
    )
    const displayName =
      conversation.name ||
      otherParticipants
        .filter((p) => p.displayName)
        .map((p) => p.displayName)
        .join(', ') ||
      'Unknown'

    const lastMessageTime = conversation.lastMessageAt
      ? formatDistanceToNow(new Date(conversation.lastMessageAt.endsWith('Z') ? conversation.lastMessageAt : conversation.lastMessageAt + 'Z'), { addSuffix: false })
      : ''

    return { otherParticipants, displayName, lastMessageTime }
  }

  const getTypeBadgeIcon = (type: ConversationType) => {
    switch (type) {
      case ConversationType.Group:
        return <Groups sx={{ fontSize: 12, color: 'success.main' }} />
      case ConversationType.Adventure:
        return <Terrain sx={{ fontSize: 12, color: 'warning.main' }} />
      default:
        return null
    }
  }

  const getTypeTagChip = (conversation: ConversationDto) => {
    if (conversation.type === ConversationType.Group) {
      return (
        <Chip
          label={`Group${conversation.participants.length > 0 ? ` (${conversation.participants.length})` : ''}`}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            bgcolor: alpha(theme.palette.success.main, 0.15),
            color: 'success.main',
            fontWeight: 600,
          }}
        />
      )
    }
    if (conversation.type === ConversationType.Adventure) {
      return (
        <Chip
          label={`Adventure${conversation.participants.length > 0 ? ` (${conversation.participants.length})` : ''}`}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            bgcolor: alpha(theme.palette.warning.main, 0.15),
            color: 'warning.main',
            fontWeight: 600,
          }}
        />
      )
    }
    return null
  }

  const renderConversationItem = (conversation: ConversationDto) => {
    const isActive = activeConversationId === conversation.id
    const { otherParticipants, displayName, lastMessageTime } = getConversationDisplayInfo(conversation)
    const hasUnread = conversation.unreadMessageCount > 0
    const badgeIcon = getTypeBadgeIcon(conversation.type)

    const avatarSx = conversation.type === ConversationType.Direct
      ? { bgcolor: 'primary.main' }
      : { bgcolor: 'primary.main', borderRadius: '14px' }

    return (
      <ListItemButton
        key={conversation.id}
        selected={isActive}
        onClick={() => onConversationSelect(conversation.id)}
        sx={{
          borderRadius: 1,
          mb: 0.5,
          mx: 1,
          ...(hasUnread && !isActive && {
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          }),
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
          },
        }}
      >
        <ListItemAvatar>
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={
                conversation.type === ConversationType.Group
                  ? conversation.groupAvatar || undefined
                  : conversation.type === ConversationType.Adventure
                    ? conversation.adventureAvatar || undefined
                    : undefined
              }
              sx={{ width: 44, height: 44, ...avatarSx }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            {badgeIcon && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                {badgeIcon}
              </Box>
            )}
          </Box>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="body2"
                fontWeight={hasUnread ? 600 : 400}
                noWrap
                sx={{ flex: 1 }}
              >
                {displayName}
              </Typography>
              {lastMessageTime && (
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {lastMessageTime}
                </Typography>
              )}
            </Box>
          }
          secondary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                component="span"
                sx={{ flex: 1 }}
              >
                {conversation.lastMessageContent || 'No messages yet'}
              </Typography>
              {getTypeTagChip(conversation)}
              {hasUnread && (
                <Badge
                  badgeContent={conversation.unreadMessageCount}
                  color="primary"
                  sx={{
                    ml: 0.5,
                    '& .MuiBadge-badge': {
                      position: 'static',
                      transform: 'none',
                      minWidth: 20,
                      height: 20,
                      borderRadius: 10,
                    },
                  }}
                />
              )}
            </Box>
          }
        />
      </ListItemButton>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
        {[1, 2, 3, 4, 5].map((i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Skeleton variant="circular" width={44} height={44} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="80%" />
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search */}
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filter Chips */}
      <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {FILTER_OPTIONS.map((option) => (
          <Chip
            key={option.key}
            label={option.label}
            size="small"
            onClick={() => setConversationFilter(option.key)}
            sx={{
              fontWeight: 600,
              ...(conversationFilter === option.key
                ? {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }
                : {
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'action.selected' },
                  }),
            }}
          />
        ))}
      </Box>

      {/* Conversation list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List disablePadding>
          {searchFiltered.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 4, px: 2 }}
            >
              {searchQuery.trim() ? 'No conversations match your search' : 'No conversations yet'}
            </Typography>
          ) : (
            searchFiltered.map(renderConversationItem)
          )}
        </List>
      </Box>
    </Box>
  )
}
